import React, { useState } from 'react';
import {
  Card,
  Button,
  Typography,
  Space,
  Spin,
  Alert,
  Row,
  Col,
  Tag,
  Modal,
  message,
  Descriptions,
  Tooltip,
  Avatar
} from 'antd';
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  BookOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  DownOutlined,
  UpOutlined,
  UserOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useEnrolledCourseDetails from 'PlatForms/SelfLMS/Hooks/Courses/useEnrolledCourseDetail';
import { selfLearnerService } from 'PlatForms/SelfLMS/Services/Courses/CourseService';

const { Title, Text } = Typography;

const EnrolledCourseDetails = () => {
  const navigate = useNavigate();
  const { enrollmentId } = useParams();

  const { enrollment, loading, error, refetch } = useEnrolledCourseDetails(enrollmentId);
  console.log("?????????????????????enrollment", enrollment);
  console.log('enrollmentId:', enrollmentId);

  const [expandedModules, setExpandedModules] = useState({});
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [moduleActionLoading, setModuleActionLoading] = useState(null);
  const [expertSupportLoading, setExpertSupportLoading] = useState(false);

  const [isExpertSupportModalVisible, setIsExpertSupportModalVisible] = useState(false);
  const [selectedCourseForPurchase, setSelectedCourseForPurchase] = useState(null);


  // Price Modal State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPriceData, setSelectedPriceData] = useState(null);

  // Logic to convert days to readable Y/M/D
  const formatDuration = (totalDays) => {
    if (!totalDays) return "Not specified";
    const years = Math.floor(totalDays / 365);
    const months = Math.floor((totalDays % 365) / 30);
    const days = (totalDays % 365) % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    return parts.join(", ");
  };


  const handlePurchaseExpertSupport = () => {
    setSelectedCourseForPurchase(enrollment.course);
    setIsExpertSupportModalVisible(true);
  };

  const handleConfirmPurchase = async () => {
    try {
      setExpertSupportLoading(true);

      const payload = {
        enrollmentId: Number(enrollmentId),
        supportId: enrollment.course?.courseExpertSupport?.id
      };

      console.log('Purchase payload:', payload);

      if (!payload.supportId) {
        toast.error('Expert support service not found.');
        return;
      }

      const response = await selfLearnerService.purchaseExpertSupport(payload);

      toast.success('Expert support purchased successfully!');
      setIsExpertSupportModalVisible(false);
      refetch();

    } catch (error) {
      console.error('Error purchasing expert support:', error);
      toast.error(
        error.response?.data?.message ||
        'Failed to purchase expert support. Please try again.'
      );
    } finally {
      setExpertSupportLoading(false);
    }
  };



  const openPriceModal = (e, course) => {
    e.stopPropagation(); // Prevents navigating to course details
    setSelectedPriceData(course);
    setIsModalVisible(true);
  };

  // Format time function
  const formatTime = (timeString) => {
    if (!timeString || timeString === '00:00:00') return 'Not specified';
    const parts = timeString.split(':').map(p => parseInt(p, 10));
    const hours = parts[0] || 0;
    const minutes = parts[1] || 0;
    if (hours === 0 && minutes === 0) return 'Not specified';
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes <= 0) return "Not specified";

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }

    if (hours > 0) {
      return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
    }

    return `${remainingMinutes} Minutes`;
  };

  // Navigation handlers
  const handleBack = () => {
    navigate('/selfLearner-dashboard/enrolled-courses');
  };

  const isAssessmentCompleted = (moduleProgress) => {
    return moduleProgress?.assessmentResult?.passed || false;
  };





  const getFirstIncompletePage = (moduleProgress) => {
    const pages = getModulePages(moduleProgress);
    return pages.find(page => !isPageCompleted(moduleProgress, page.id));
  };

  const navigateToAssessment = async (moduleProgress) => {
    try {

      // NEW: Check if assessment is already passed
      if (moduleProgress?.assessmentResult?.passed) {
        toast.info("You have already passed this assessment.");
        setAssessmentLoading(false);
        return;
      }
      setAssessmentLoading(true);

      // 1️⃣ Check for any incomplete pages
      const firstIncompletePage = getFirstIncompletePage(moduleProgress);
      if (firstIncompletePage) {
        // If unread pages exist, navigate user there instead
        handleStartPage(moduleProgress, firstIncompletePage.id);
        toast.info("Please complete all pages before attempting the assessment.");
        setAssessmentLoading(false);
        return;
      }

      // 2️⃣ If all pages completed, proceed to assessment
      const assessmentData = await selfLearnerService.getLearnerPageData(moduleProgress.moduleId, null, true);
      const assessmentId = assessmentData?.assessmentPage?.id;

      if (!assessmentId) {
        toast.error("Assessment not available");
        setAssessmentLoading(false);
        return;
      }

      navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${moduleProgress.id}/assessment`, {
        state: {
          moduleId: moduleProgress.moduleId,
          assessmentId: assessmentId,
          moduleProgressId: moduleProgress.id,
          enrollmentId: enrollmentId,
          assessmentTitle: assessmentData.assessmentPage?.title,
          assessmentData: assessmentData
        }
      });

    } catch (error) {
      console.error("Failed to navigate to assessment:", error);
      toast.error("Failed to load assessment");
    } finally {
      setAssessmentLoading(false);
    }
  };

  // FIXED: Use the correct page ID from the module progress object
  const handleStartPage = (moduleProgress, pageId) => {
    const moduleId = moduleProgress.moduleId;
    const moduleProgressId = moduleProgress.id;

    console.log('Navigating to page:', {
      enrollmentId,
      moduleId,
      moduleProgressId,
      pageId
    });

    navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${moduleProgressId}/pages/${pageId}`, {
      state: {
        moduleId: moduleId
      }
    });
  };

  const handleStartModule = (moduleProgress) => {
    if (!moduleProgress || !moduleProgress.id) {
      toast.error('Module information is missing.');
      return;
    }

    // Navigate to first page of the module
    const firstPage = getModulePages(moduleProgress)[0];
    if (firstPage) {
      handleStartPage(moduleProgress, firstPage.id);
    }
  };

  // NEW: Handle module action (Start/Continue/Review)
  const handleModuleAction = async (moduleProgress) => {
    try {
      setModuleActionLoading(moduleProgress.id);

      // If module is completed, just navigate to the first page for review
      if (moduleProgress.isCompleted) {
        const firstPage = getModulePages(moduleProgress)[0];
        if (firstPage) {
          handleStartPage(moduleProgress, firstPage.id);
        }
        return;
      }

      // For in-progress modules, try to get the continue learning data
      try {
        const response = await selfLearnerService.startCourseEnrollment(enrollmentId);

        // Check if it's an assessment page
        if (response.isAssessmentPage) {
          // Navigate to assessment page
          navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${response.moduleId}/assessment`, {
            state: {
              moduleId: response.moduleId,
              assessmentId: response.assessmentPage.id,
              moduleProgressId: response.moduleId,
              enrollmentId: enrollmentId,
              assessmentTitle: response.assessmentPage.title,
              assessmentData: response
            }
          });
        } else {
          // Handle regular page
          let pageId;

          if (response.currentPageId) {
            pageId = response.currentPageId;
          } else if (response.hasPrevious && response.previousPageId) {
            pageId = response.previousPageId;
          } else if (response.hasNext && response.nextPageId) {
            pageId = response.nextPageId;
          } else {
            // Fallback: use the module ID if no page ID is available
            pageId = response.moduleId;
          }

          // Navigate to the page detail page
          navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${response.moduleId}/pages/${pageId}`, {
            state: {
              moduleId: response.moduleId
            }
          });
        }
      } catch (apiError) {
        console.error('API call failed, falling back to module navigation:', apiError);
        // If API fails, fall back to navigating to the first incomplete page
        const firstIncompletePage = getFirstIncompletePage(moduleProgress);
        if (firstIncompletePage) {
          handleStartPage(moduleProgress, firstIncompletePage.id);
        } else {
          // If no incomplete page, start the module from the beginning
          handleStartModule(moduleProgress);
        }
      }
    } catch (error) {
      console.error('Error in module action:', error);
      message.error('Failed to start module. Please try again.');
    } finally {
      setModuleActionLoading(null);
    }
  };

  // Helpers
  const toggleModuleExpand = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };


  const getModulePages = (moduleProgress) => {

    if (moduleProgress?.pages && moduleProgress.pages.length > 0) {
      return moduleProgress.pages.map((page) => ({
        ...page,
        id: page.id,
        pageNumber: page.pageNumber,
        title: page.title || `Page ${page.pageNumber}`,
        estimatedTime: page.estimatedTime || '00:15:00'
      }));
    }


    const modulePages = moduleProgress?.module?.pages;
    if (modulePages && modulePages.length > 0) {
      return modulePages.map((p, idx) => ({
        ...p,
        id: p.id ?? idx + 1,
        pageNumber: p.pageNumber ?? idx + 1,
      }));
    }

    // Last resort fallback
    const pageCount = moduleProgress?.module?.pagesCount ?? 5;
    return Array.from({ length: pageCount }, (_, i) => ({
      id: i + 1,
      pageNumber: i + 1,
      title: `Page ${i + 1} of ${moduleProgress.module?.title ?? ''}`,
      estimatedTime: '00:15:00',
    }));
  };

  // UPDATED: Check if a page is completed using the pageProgresses array
  const isPageCompleted = (moduleProgress, pageId) => {
    if (moduleProgress?.pageProgresses && moduleProgress.pageProgresses.length > 0) {
      const pageProgress = moduleProgress.pageProgresses.find(pp => pp.pageId === pageId);
      return pageProgress?.isViewed || false;
    }

    // Fallback to completedPages count
    if (typeof moduleProgress.completedPages === 'number') {
      const pageIndex = getModulePages(moduleProgress).findIndex(p => p.id === pageId);
      return pageIndex < moduleProgress.completedPages;
    }

    return false;
  };


  const calculateModuleProgress = (moduleProgress) => {
    if (!moduleProgress?.module?.pages?.length) {
      if (typeof moduleProgress.completedPages === 'number' && moduleProgress.module?.pagesCount) {
        return Math.round((moduleProgress.completedPages / moduleProgress.module.pagesCount) * 100);
      }
      return 0;
    }
    return Math.round((moduleProgress.completedPages / moduleProgress.module.pages.length) * 100);
  };

  // Loading / error states
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Course Details"
        description={error}
        type="error"
        showIcon
        style={{ margin: 20 }}
        action={<Button size="small" onClick={handleBack}>Go Back</Button>}
      />
    );
  }

  if (!enrollment) {
    return (
      <Alert
        message="Course Not Found"
        description="The requested course could not be found."
        type="error"
        showIcon
        style={{ margin: 20 }}
        action={<Button size="small" onClick={handleBack}>Go Back</Button>}
      />
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f7fafc',
      padding: '8px'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Main Course Details Card */}
        <Card
          title="Course Details"
          extra={
            <Button
              type="default"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              size="small"
              style={{ marginRight: '40px' }}
            >
              Back to Courses
            </Button>
          }
          style={{
            border: '2px solid #cdd6e2ff',
            borderTop: 'none',
            borderRadius: 0,
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            marginBottom: 16,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* CLICKABLE PRICE/FREE BANNER */}
          <div
            onClick={(e) => openPriceModal(e, enrollment.course)}
            style={{
              position: "absolute",
              top: 18,
              right: -35,
              background: enrollment.course?.isPaid ? "#13c2c2" : "#52c41a",
              color: "white",
              padding: "4px 0",
              transform: "rotate(45deg)",
              fontSize: "11px",
              fontWeight: "bold",
              zIndex: 10,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              textAlign: "center",
              width: "130px",
              cursor: "pointer",
              lineHeight: "1.2"
            }}
          >
            {enrollment.course?.isPaid ? (
              `${enrollment.course?.pricing?.currency === "USD" ? "$" : (enrollment.course?.pricing?.currency || "$")}${enrollment.course?.pricing?.price || 0}`
            ) : (
              "FREE"
            )}
          </div>

          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Title level={3} style={{ margin: 0, fontSize: 18, color: '#2d3748' }}>
                {enrollment.course?.title}
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {enrollment.course?.description}
              </Text>
            </div>

            {/* Compact Stats Row */}
            <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
              <Col xs={12}>
                <div style={{ textAlign: 'center', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <BookOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Modules</div>
                  <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1890ff' }}>
                    {enrollment.moduleProgresses?.length || 0}
                  </div>
                </div>
              </Col>

              <Col xs={12}>
                <div style={{ textAlign: 'center', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Completed</div>
                  <div style={{ fontSize: 14, fontWeight: 'bold', color: '#52c41a' }}>
                    {enrollment.moduleProgresses?.filter(m => m.isCompleted).length || 0}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Expert Support Section - Enhanced with Expert Details */}
            {enrollment.expertSupportNeeded && (
              <div style={{
                padding: '12px',
                background: enrollment.hasExpertSupportPurchased ? '#f0f9ff' : '#fff7e6',
                borderRadius: 4,
                marginTop: 12,
                border: `1px solid ${enrollment.hasExpertSupportPurchased ? '#91d5ff' : '#ffd591'}`,
              }}>
                {/* Case 1: Support is already purchased */}
                {enrollment.hasExpertSupportPurchased ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <CheckCircleOutlined style={{ fontSize: 20, color: '#1890ff', marginRight: 8 }} />
                      <div>
                        <Text strong style={{ color: '#1890ff', fontSize: 14 }}>Expert Support Active</Text>
                        <div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>
                          Get personalized guidance from experts
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Tag color="blue" style={{ margin: 0, fontSize: 10, padding: '1px 6px' }}>
                        {enrollment.expertServicePurchase?.consumedMinutes || 0}/{enrollment.expertServicePurchase?.totalMinutes} min
                      </Tag>
                      <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
                        {enrollment.expertServicePurchase?.expert?.firstName?.charAt(0)}.
                        {enrollment.expertServicePurchase?.expert?.lastName}
                      </div>
                    </div>
                  </div>
                ) : enrollment.canPurchaseExpertService ? (
                  // Case 2: Support is available for purchase
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Expert Info Card */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px',
                      background: '#f0f9ff',
                      border: '1px solid #bae7ff',
                      borderRadius: 6
                    }}>
                      <Avatar
                        size={48}
                        src={enrollment.course?.courseExpertSupport?.expert?.profileImage}
                        icon={<UserOutlined />}
                      />
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: 14, display: 'block' }}>
                          {enrollment.course?.courseExpertSupport?.expert?.firstName} {enrollment.course?.courseExpertSupport?.expert?.lastName}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {enrollment.course?.courseExpertSupport?.expert?.yearsOfExperience} years of experience
                        </Text>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#1890ff', fontSize: 18, fontWeight: 'bold' }}>
                          ${enrollment.course?.courseExpertSupport?.unitPrice}
                        </div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          per {enrollment.course?.courseExpertSupport?.unitDurationMinutes} min session
                        </Text>
                      </div>
                    </div>

                    {/* Service Description */}
                    <div style={{
                      padding: '8px 12px',
                      background: '#fffbe6',
                      borderRadius: 3,
                      borderLeft: '3px solid #faad14',
                      fontSize: '12px'
                    }}>
                      <Text strong style={{ display: 'block', marginBottom: 4, color: '#8c6400' }}>
                        {enrollment.course?.courseExpertSupport?.title}
                      </Text>
                      <Text style={{ fontSize: 12, color: '#666' }}>
                        {enrollment.course?.courseExpertSupport?.description}
                      </Text>
                    </div>

                    <Button
                      type="primary"
                      icon={<StarOutlined />}
                      onClick={handlePurchaseExpertSupport}
                      loading={expertSupportLoading}
                      style={{
                        width: '100%',
                        fontWeight: '500',
                        height: 40,
                        borderRadius: 6,
                        background: '#1890ff',
                        borderColor: '#1890ff'
                      }}
                    >
                      Get Expert Help
                    </Button>
                  </div>
                ) : (
                  // Case 3: Support is not available
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <UserOutlined style={{ fontSize: 20, color: '#faad14', marginRight: 8 }} />
                      <div>
                        <Text strong style={{ color: '#333' }}>Need Expert Help?</Text>
                        <div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>
                          Expert support will be available for this course soon.
                        </div>
                      </div>
                    </div>
                    <Text type="secondary" style={{ fontSize: '11px', background: '#f5f5f5', padding: '4px 8px', borderRadius: 4 }}>
                      Coming Soon
                    </Text>
                  </div>
                )}
              </div>
            )}

            {/* Course Metadata */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid #f0f0f0', marginTop: 8 }}>
              <Space size="middle">
                <Space size="small">
                  <ClockCircleOutlined style={{ color: "#666", fontSize: 12 }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatTime(enrollment.course?.estimatedTime)}
                  </Text>
                </Space>
                <Space size="small">
                  <FileTextOutlined style={{ color: "#666", fontSize: 12 }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {enrollment.moduleProgresses?.length || 0} modules
                  </Text>
                </Space>
              </Space>

              {enrollment.isCompleted && (
                <Tag color="blue" icon={<CheckCircleOutlined />} style={{ fontSize: 11 }}>
                  Completed
                </Tag>
              )}
            </div>
          </Space>
        </Card>



        {/* Modules Card */}
        <Card
          style={{
            border: '2px solid #cdd6e2ff',
            borderTop: 'none',
            borderRadius: 0,
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, margin: 0, color: '#2d3748' }}>Course Modules</h2>
            </div>

            {/* Modules List */}
            {enrollment.moduleProgresses?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {enrollment.moduleProgresses.map((moduleProgress, index) => {
                  const modulePages = getModulePages(moduleProgress);
                  console.log("...........moduleProgress ", moduleProgress);
                  const isExpanded = !!expandedModules[moduleProgress.moduleId];

                  const hasAssessment = moduleProgress.module?.hasAssessment;
                  const assessmentCompleted = isAssessmentCompleted(moduleProgress);

                  return (
                    <Card
                      key={moduleProgress.moduleId}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                      bodyStyle={{ padding: 16 }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                            <Text strong style={{ fontSize: 16, color: '#2d3748' }}>
                              {index + 1}. {moduleProgress.module?.title}
                            </Text>

                            {/* NEW: Continue Learning Button */}
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlayCircleOutlined />}
                              onClick={() => handleModuleAction(moduleProgress)}
                              loading={moduleActionLoading === moduleProgress.id}
                            >
                              {moduleProgress.isCompleted ? 'Review' :
                                moduleProgress.completedPages > 0 ? 'Continue' : 'Start'}
                            </Button>
                          </div>

                          {moduleProgress.isCompleted && (
                            <Tag color="blue" style={{ fontSize: 10, marginTop: 4 }}>
                              <span style={{ color: 'blue' }}>✔</span> Completed
                            </Tag>
                          )}

                          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                            {moduleProgress.module?.description}
                          </Text>

                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <Space size="small">
                              <ClockCircleOutlined style={{ color: '#666', fontSize: 12 }} />
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {formatTime(moduleProgress.module?.estimatedTime)}
                              </Text>
                            </Space>

                            <Space size="small">
                              <BookOutlined style={{ color: '#666', fontSize: 12 }} />
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {modulePages.length} pages
                              </Text>
                            </Space>

                            {hasAssessment && (
                              <Tag color="blue" style={{ fontSize: 10 }}>Has Assessment</Tag>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 'bold'
                          }}>
                            {moduleProgress.isCompleted ? '✓' : `${calculateModuleProgress(moduleProgress)}%`}
                          </div>
                        </div>
                      </div>

                      {/* Overview Toggle & Page List */}
                      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                        <Button
                          type="text"
                          onClick={() => toggleModuleExpand(moduleProgress.moduleId)}
                          style={{
                            padding: '4px 8px',
                            fontSize: 12,
                            height: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          <span>Overview</span>
                          {isExpanded ? <UpOutlined /> : <DownOutlined />}
                        </Button>

                        {isExpanded && (
                          <div style={{ marginTop: 12 }}>
                            {modulePages.map((page, pageIndex) => {
                              // UPDATED: Use the isPageCompleted function to check if page is completed
                              const isPageCompletedStatus = isPageCompleted(moduleProgress, page.id);

                              return (
                                <div
                                  key={page.id}
                                  style={{
                                    padding: '12px 8px',
                                    borderLeft: isPageCompletedStatus ? '4px solid #1a45c4ff' : '4px solid #d9d9d9',
                                    cursor: 'pointer',
                                    marginBottom: 4,
                                    transition: 'all 0.2s'
                                  }}
                                  onClick={() => handleStartPage(moduleProgress, page.id)}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fafafa'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <div style={{ flex: 1 }}>
                                      <Text style={{
                                        fontSize: 13,
                                        display: 'block',
                                        color: '#2d3748',
                                        fontWeight: isPageCompletedStatus ? 500 : 'normal'
                                      }}>
                                        {page.title || `Page ${page.pageNumber || pageIndex + 1}`}
                                      </Text>
                                      <Text type="secondary" style={{ fontSize: 11, marginTop: 2 }}>
                                        {formatTime(page.estimatedTime)}
                                      </Text>
                                    </div>

                                    {isPageCompletedStatus && (
                                      <span style={{ color: 'blue' }}>✔</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                            {/* Assessment Card */}
                            {hasAssessment && (
                              <div
                                style={{
                                  padding: '12px 8px',
                                  borderLeft: assessmentCompleted ? '4px solid #1a56c4ff' : '4px solid #d9d9d9',
                                  cursor: 'pointer',
                                  marginBottom: 4,
                                  transition: 'all 0.2s',
                                }}
                                onClick={() => navigateToAssessment(moduleProgress)}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fafafa'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = assessmentCompleted ? '#f6ffed' : '#fff'; }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                  <div style={{ flex: 1 }}>
                                    <Text style={{
                                      fontSize: 13,
                                      display: 'block',
                                      color: '#2d3748',
                                      fontWeight: assessmentCompleted ? 500 : 'normal'
                                    }}>
                                      Assessment
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 11, marginTop: 2 }}>
                                      {moduleProgress.assessmentResult ?
                                        `Score: ${moduleProgress.assessmentResult.score}%` :
                                        'Test your knowledge'
                                      }
                                    </Text>
                                  </div>

                                  {assessmentCompleted && (
                                    <span style={{ color: 'blue' }}>✔</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card
                style={{
                  border: '2px solid #cdd6e2ff',
                  borderTop: 'none',
                  borderRadius: 0,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                }}
                bodyStyle={{ padding: 20, textAlign: 'center' }}
              >
                <BookOutlined style={{ fontSize: 36, color: '#d9d9d9', marginBottom: 12 }} />
                <Title level={4} style={{ color: '#666', fontSize: 16 }}>No Modules Available</Title>
                <Text style={{ color: '#666', fontSize: 13 }}>This course does not have any modules yet.</Text>
              </Card>
            )}
          </div>
        </Card>
      </div>
      {/* PRICE DETAIL MODAL */}
      <Modal
        title="Pricing & Access Information"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[<Button key="close" type="primary" onClick={() => setIsModalVisible(false)}>Got it</Button>]}
      >
        {selectedPriceData && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Price">
              <Text strong type="success" style={{ fontSize: '16px' }}>
                {selectedPriceData.pricing?.currency} {selectedPriceData.pricing?.price}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Access Duration">
              {formatDuration(selectedPriceData.pricing?.accessDurationDays)}
            </Descriptions.Item>

          </Descriptions>
        )}
      </Modal>
      {/* EXPERT SUPPORT MODAL */}
      <Modal
        title="Expert Support Purchase"
        open={isExpertSupportModalVisible}
        onCancel={() => setIsExpertSupportModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsExpertSupportModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="purchase"
            type="primary"
            onClick={handleConfirmPurchase}
            loading={expertSupportLoading}
          >
            Get Expert Help
          </Button>
        ]}
        width={600}
      >
        {selectedCourseForPurchase && (
          <div>
            <Title level={4}>{selectedCourseForPurchase.title}</Title>

            <div style={{
              padding: '16px',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <Title level={5} style={{ marginBottom: '8px' }}>
                <UserOutlined /> Expert Support Service
              </Title>

              <div style={{ marginBottom: '12px' }}>
                <Avatar
                  src={selectedCourseForPurchase.courseExpertSupport?.expert?.profileImage}
                  icon={<UserOutlined />}
                  size="small"
                  style={{ marginRight: '8px' }}
                />
                <Text strong>
                  {selectedCourseForPurchase.courseExpertSupport?.expert?.firstName} {selectedCourseForPurchase.courseExpertSupport?.expert?.lastName}
                </Text>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {selectedCourseForPurchase.courseExpertSupport?.expert?.yearsOfExperience} years of experience
                </div>
              </div>

              <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                {selectedCourseForPurchase.courseExpertSupport?.description}
              </Text>

              <div style={{ marginTop: '8px' }}>
                <Text strong>
                  ${selectedCourseForPurchase.courseExpertSupport?.unitPrice}/
                  {selectedCourseForPurchase.courseExpertSupport?.unitDurationMinutes}Minutes
                </Text>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Includes {selectedCourseForPurchase.courseExpertSupport?.allowedUnits} sessions
                </div>
              </div>
            </div>

            <div>
              <Text>Are you sure you want to purchase expert support for this course?</Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EnrolledCourseDetails;